import { Card, Elevation, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';
import { alphaSort, isNotEqual } from 'vet-bones/utils';

import { getOverviewDrawerViewItemTestId } from 'src/constants/testIds';
import {
  prevProjectRulesByIdSelector,
  projectInvalidRulesByIdSelector,
  projectRulesByIdSelector,
} from 'src/selectors/projectRules';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectRuleOverviewDrawerView: React.VFC = () => {
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();

  const invalidRulesById = useReduxSelector(
    projectInvalidRulesByIdSelector(projectId)
  );
  const rulesById = useReduxSelector(projectRulesByIdSelector(projectId));
  const prevRulesById = useReduxSelector(
    prevProjectRulesByIdSelector(projectId)
  );

  const handleEdit = React.useCallback(
    (ruleId: string) => {
      dispatch(
        uiSlice.actions.toggledRuleDetailDrawer({ isOpen: true, ruleId })
      );
    },
    [dispatch]
  );

  const list = Object.values(rulesById)
    .sort(alphaSort(({ name }) => name))
    .sort((a, b) => {
      if (invalidRulesById[a.id] && !invalidRulesById[b.id]) {
        return -1;
      }

      if (!invalidRulesById[a.id] && invalidRulesById[b.id]) {
        return 1;
      }

      return 0;
    })
    .map((rule, idx) => (
      <Card
        key={rule.id}
        className={classNames(
          'sd-drawer-project-rule-overview__view__card',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted':
              prevRulesById && isNotEqual(prevRulesById?.[rule.id], rule),
          }
        )}
        data-testid={getOverviewDrawerViewItemTestId(rule.id, idx)}
        elevation={Elevation.ONE}
        interactive
        onClick={() => {
          handleEdit(rule.id);
        }}
      >
        <Text ellipsize>{rule.name}</Text>
        {invalidRulesById[rule.id] ? (
          <Tooltip
            content={copy.components.projectRuleOverviewDrawer.invalidRule}
          >
            <Icon icon={IconNames.WARNING_SIGN} />
          </Tooltip>
        ) : null}
      </Card>
    ));

  return <div className="sd-drawer-project-rule-overview__view">{list}</div>;
};
